/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import {
  DEFAULT_QUERY,
  PAGE_SIZES,
  type FilterSection,
  type QuarantineFilters,
  type QuarantineQuery,
  type QuarantineSortField,
} from './quarantine.types';

export type UrlParams = Record<string, string | undefined | null>;

const SORT_FIELDS: QuarantineSortField[] = ['QUARANTINE_TIME', 'SEVERITY', 'COMPONENT', 'REPOSITORY', 'POLICY'];

const FILTER_SECTIONS: FilterSection[] = ['severity', 'policy', 'ecosystem', 'repository'];

/** Each value is percent-encoded before joining, so a name that contains a comma survives. */
const decode = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const list = (value: string | undefined | null): string[] =>
  value ? value.split(',').filter(Boolean).map(decode) : [];

const join = (values: string[]): string | undefined => values.map(encodeURIComponent).join(',') || undefined;

const positive = (value: string | undefined | null, fallback: number): number => {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
};

export function parseQuarantineUrl(params: UrlParams): QuarantineQuery {
  const filters = Object.fromEntries(FILTER_SECTIONS.map((section) => [section, list(params[section])])) as QuarantineFilters;
  const pageSize = Number(params.pageSize);
  return {
    filters,
    search: params.q ?? '',
    sortBy: SORT_FIELDS.includes(params.sort as QuarantineSortField)
      ? (params.sort as QuarantineSortField)
      : DEFAULT_QUERY.sortBy,
    asc: params.dir === 'asc',
    page: positive(params.page, DEFAULT_QUERY.page),
    pageSize: (PAGE_SIZES as readonly number[]).includes(pageSize) ? pageSize : DEFAULT_QUERY.pageSize,
  };
}

/** Defaults are dropped so the URL stays short. */
export function serializeQuarantineUrl(query: QuarantineQuery): UrlParams {
  return {
    severity: join(query.filters.severity),
    policy: join(query.filters.policy),
    ecosystem: join(query.filters.ecosystem),
    repository: join(query.filters.repository),
    q: query.search || undefined,
    sort: query.sortBy !== DEFAULT_QUERY.sortBy ? query.sortBy : undefined,
    dir: query.asc ? 'asc' : undefined,
    page: query.page > 1 ? String(query.page) : undefined,
    pageSize: query.pageSize !== DEFAULT_QUERY.pageSize ? String(query.pageSize) : undefined,
  };
}

/** The list's own query string, so the detail page can link back to the same page of results. */
export function quarantineBackParams(query: QuarantineQuery): string {
  const search = new URLSearchParams();
  Object.entries(serializeQuarantineUrl(query)).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      search.set(key, value);
    }
  });
  return search.toString();
}
